import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getRecurringBlocksApi, createRecurringBlockApi, updateRecurringBlockApi, regenerateRecurringBlockApi, deleteRecurringSeriesApi, getCourtsApi } from '../lib/api';
import { formatHour, getTodayPHT } from '../lib/format';
import { ArrowLeft, Plus, Repeat, Pause, Play, Trash2, RefreshCw, Calendar, Clock, MapPin, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = Array.from({ length: 18 }, (_, i) => i + 6); // 6 AM to 11 PM

export default function RecurringBlocks() {
  const navigate = useNavigate();
  const { user, loading: authLoading, hasPermission } = useAuth();
  const [series, setSeries] = useState([]);
  const [courts, setCourts] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formCourtIds, setFormCourtIds] = useState([]);
  const [formAllCourts, setFormAllCourts] = useState(false);
  const [formStartHour, setFormStartHour] = useState('');
  const [formEndHour, setFormEndHour] = useState('');
  const [formWeekdays, setFormWeekdays] = useState([]);
  const [formStartDate, setFormStartDate] = useState(() => getTodayPHT());
  const [formEndDate, setFormEndDate] = useState('');
  const [formInterval, setFormInterval] = useState('1');
  const [submitting, setSubmitting] = useState(false);
  const [conflicts, setConflicts] = useState([]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    loadData();
  }, [user, authLoading, navigate]);

  const loadData = async () => {
    try {
      const [seriesRes, courtsRes] = await Promise.all([
        getRecurringBlocksApi(),
        getCourtsApi(),
      ]);
      setSeries(seriesRes.data);
      setCourts(courtsRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    }
    setLoading(false);
  };

  const resetForm = () => {
    setFormTitle('');
    setFormCourtIds([]);
    setFormAllCourts(false);
    setFormStartHour('');
    setFormEndHour('');
    setFormWeekdays([]);
    setFormStartDate(new Date().toISOString().split('T')[0]);
    setFormEndDate('');
    setFormInterval('1');
    setConflicts([]);
  };

  const toggleWeekday = (day) => {
    setFormWeekdays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  const toggleCourt = (courtId) => {
    setFormCourtIds(prev => prev.includes(courtId) ? prev.filter(c => c !== courtId) : [...prev, courtId]);
  };

  const handleCreate = async () => {
    if (!formTitle.trim()) { toast.error('Enter a title'); return; }
    if (!formStartHour || !formEndHour) { toast.error('Select start and end time'); return; }
    if (formWeekdays.length === 0) { toast.error('Select at least one day'); return; }
    if (!formAllCourts && formCourtIds.length === 0) { toast.error('Select at least one court'); return; }

    setSubmitting(true);
    try {
      const res = await createRecurringBlockApi({
        title: formTitle.trim(),
        court_ids: formAllCourts ? ['all'] : formCourtIds,
        start_hour: parseInt(formStartHour),
        end_hour: parseInt(formEndHour),
        weekdays: formWeekdays.sort(),
        start_date: formStartDate,
        end_date: formEndDate || null,
        repeat_interval: parseInt(formInterval),
      });

      if (res.data.conflicts?.length > 0) {
        setConflicts(res.data.conflicts);
        toast.warning(`Created with ${res.data.conflicts.length} conflicts — some slots already booked`);
      } else {
        toast.success(`Created! ${res.data.instances_created} blocks scheduled`);
        setCreateOpen(false);
        resetForm();
      }
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create recurring block');
    }
    setSubmitting(false);
  };

  const handlePauseResume = async (s) => {
    try {
      await updateRecurringBlockApi(s.id, { is_paused: !s.is_paused });
      if (!s.is_paused) {
        toast.success('Series paused');
      } else {
        await regenerateRecurringBlockApi(s.id);
        toast.success('Series resumed — blocks regenerated');
      }
      loadData();
    } catch (err) {
      toast.error('Failed to update series');
    }
  };

  const handleDelete = async (s) => {
    try {
      await deleteRecurringSeriesApi(s.id);
      toast.success('Series and all blocks deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete series');
    }
  };

  const handleRegenerate = async (s) => {
    try {
      const res = await regenerateRecurringBlockApi(s.id);
      toast.success(`Regenerated ${res.data.instances_created} blocks`);
      loadData();
    } catch (err) {
      toast.error('Failed to regenerate');
    }
  };

  if (authLoading || !user) return null;

  return (
    <div className="min-h-screen bg-app-bg" data-testid="recurring-blocks-page">
      {/* Header */}
      <div className="bg-white border-b border-black/5 px-4 py-3 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              data-testid="recurring-back-btn"
              onClick={() => navigate('/operator')}
              className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <h1 className="font-heading text-xl font-bold text-gray-900">Recurring Blocks</h1>
          </div>
          {hasPermission('block_court') && (
            <Button
              data-testid="create-recurring-btn"
              onClick={() => { resetForm(); setCreateOpen(true); }}
              className="bg-coral hover:bg-coral/90 text-white rounded-xl"
            >
              <Plus className="w-4 h-4 mr-1" /> New Series
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {loading ? (
          <div className="text-center text-gray-400 py-12">Loading...</div>
        ) : series.length === 0 ? (
          <div className="text-center py-16" data-testid="no-recurring-blocks">
            <Repeat className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="font-heading text-lg font-semibold text-gray-800 mb-1">No recurring blocks</p>
            <p className="text-sm text-gray-500">Create one to reserve courts for regular events</p>
          </div>
        ) : (
          series.map((s) => (
            <div key={s.id} className="bg-white rounded-3xl border border-black/5 p-5" data-testid={`series-${s.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading text-lg font-semibold text-gray-900">{s.title}</h3>
                    {s.is_paused && <Badge className="bg-status-held-bg text-status-held">Paused</Badge>}
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {formatHour(s.start_hour)}–{formatHour(s.end_hour - 1 || s.end_hour)}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {s.court_ids?.includes('all') ? 'All Courts' : (s.court_names || []).join(', ')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Weekdays */}
              <div className="flex gap-1.5 mb-3">
                {WEEKDAY_NAMES.map((name, i) => (
                  <span
                    key={i}
                    className={`text-xs px-2.5 py-1 rounded-lg font-medium ${
                      (s.weekdays || []).includes(i)
                        ? 'bg-coral text-white'
                        : 'bg-gray-100 text-gray-300'
                    }`}
                  >
                    {name}
                  </span>
                ))}
              </div>

              {/* Meta */}
              <div className="flex items-center gap-3 text-xs text-gray-400 mb-4">
                <span>Every {s.repeat_interval > 1 ? `${s.repeat_interval} weeks` : 'week'}</span>
                <span>From {s.start_date}</span>
                {s.end_date && <span>Until {s.end_date}</span>}
                {!s.end_date && <span>No end date</span>}
              </div>

              {/* Actions */}
              {hasPermission('block_court') && (
                <div className="flex gap-2 flex-wrap">
                  <Button
                    data-testid={`pause-${s.id}`}
                    onClick={() => handlePauseResume(s)}
                    variant="outline"
                    size="sm"
                    className="rounded-lg text-xs"
                  >
                    {s.is_paused ? <Play className="w-3 h-3 mr-1" /> : <Pause className="w-3 h-3 mr-1" />}
                    {s.is_paused ? 'Resume' : 'Pause'}
                  </Button>
                  <Button
                    data-testid={`regenerate-${s.id}`}
                    onClick={() => handleRegenerate(s)}
                    variant="outline"
                    size="sm"
                    className="rounded-lg text-xs"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" /> Regenerate
                  </Button>
                  <Button
                    data-testid={`delete-${s.id}`}
                    onClick={() => handleDelete(s)}
                    variant="outline"
                    size="sm"
                    className="rounded-lg text-xs text-blocks border-blocks/30 hover:bg-blocks-tint"
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Delete Series
                  </Button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-recurring-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">New Recurring Block</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Title */}
            <div>
              <Label className="text-sm text-gray-600">Title</Label>
              <Input
                data-testid="rb-title"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="e.g. Sunday Worship, Tuesday Maintenance"
                className="rounded-xl mt-1"
              />
            </div>

            {/* Courts */}
            <div>
              <Label className="text-sm text-gray-600">Courts</Label>
              <div className="flex items-center gap-3 mt-1 mb-2">
                <Switch
                  data-testid="rb-all-courts"
                  checked={formAllCourts}
                  onCheckedChange={(val) => { setFormAllCourts(val); if (val) setFormCourtIds([]); }}
                />
                <span className="text-sm text-gray-600">All courts</span>
              </div>
              {!formAllCourts && (
                <div className="flex gap-2 flex-wrap">
                  {courts.map(court => (
                    <button
                      key={court.id}
                      data-testid={`rb-court-${court.id}`}
                      onClick={() => toggleCourt(court.id)}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                        formCourtIds.includes(court.id)
                          ? 'bg-coral text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {court.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-gray-600">Start Time</Label>
                <Select value={formStartHour} onValueChange={setFormStartHour}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="rb-start-hour">
                    <SelectValue placeholder="Start" />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.map(h => (
                      <SelectItem key={h} value={String(h)}>{formatHour(h)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-gray-600">Block through</Label>
                <Select value={formEndHour} onValueChange={setFormEndHour}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="rb-end-hour">
                    <SelectValue placeholder="End" />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.filter(h => {
                      const start = parseInt(formStartHour);
                      return isNaN(start) || h >= start;
                    }).map(h => (
                      <SelectItem key={h} value={String(h + 1)}>{formatHour(h)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Weekdays */}
            <div>
              <Label className="text-sm text-gray-600 mb-2 block">Repeat on</Label>
              <div className="flex gap-1.5">
                {WEEKDAY_NAMES.map((name, i) => (
                  <button
                    key={i}
                    data-testid={`rb-day-${i}`}
                    onClick={() => toggleWeekday(i)}
                    className={`flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                      formWeekdays.includes(i)
                        ? 'bg-coral text-white shadow-md'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>

            {/* Repeat interval */}
            <div>
              <Label className="text-sm text-gray-600">Repeat every</Label>
              <Select value={formInterval} onValueChange={setFormInterval}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="rb-interval">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Every week</SelectItem>
                  <SelectItem value="2">Every 2 weeks</SelectItem>
                  <SelectItem value="3">Every 3 weeks</SelectItem>
                  <SelectItem value="4">Every 4 weeks</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-gray-600">Start Date</Label>
                <Input
                  data-testid="rb-start-date"
                  type="date"
                  value={formStartDate}
                  onChange={(e) => setFormStartDate(e.target.value)}
                  className="rounded-xl mt-1"
                />
              </div>
              <div>
                <Label className="text-sm text-gray-600">End Date <span className="text-gray-400">(optional)</span></Label>
                <Input
                  data-testid="rb-end-date"
                  type="date"
                  value={formEndDate}
                  onChange={(e) => setFormEndDate(e.target.value)}
                  className="rounded-xl mt-1"
                />
              </div>
            </div>

            {/* Conflicts Warning */}
            {conflicts.length > 0 && (
              <div className="bg-status-held-bg rounded-2xl border border-status-held/30 p-4" data-testid="conflicts-warning">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-status-held" />
                  <span className="text-sm font-semibold text-status-held">Some slots had conflicts</span>
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {conflicts.map((c, i) => (
                    <p key={i} className="text-xs text-status-held">
                      {c.date} · {c.court_name} · {c.hours}
                    </p>
                  ))}
                </div>
                <p className="text-xs text-status-held mt-2">These times were skipped. Resolve the bookings manually if needed.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              data-testid="rb-submit-btn"
              onClick={handleCreate}
              disabled={submitting}
              className="bg-coral hover:bg-coral/90 text-white rounded-xl px-8"
            >
              {submitting ? 'Creating...' : 'Create Recurring Block'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
